import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Eye,
  FileText,
  DollarSign,
  Percent,
  Mail,
  MessageSquare,
  Copy,
  Edit,
  Trash2,
  ShoppingCart,
  Receipt
} from 'lucide-react';
import { RowActionsMenu, RowAction } from '../../../../../components/StandardPage';
import { SteelOrder } from '../types';
import { deleteSteelOrder, closeSaleOrder, duplicateSteelOrder, updateSteelOrder } from '../api';
import ConfirmationModal from '../../../../../components/ModalConfirmation';
import CloseSaleModal from '../CloseSaleModal/index';
import { FeedbackContext } from '../../../../../contexts/FeedbackContext';
import ModalContext from '../../../../../contexts/ModalContext';
import ApiService from '../../../../../services/ApiService';

interface ActionsDropdownProps {
  order: SteelOrder;
}

const ActionsDropdown: React.FC<ActionsDropdownProps> = ({ order }) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCloseSaleModal, setShowCloseSaleModal] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setFeedback } = useContext(FeedbackContext);
  const { setModalActive, setModalSection, setSelectedSteelOrder } = useContext(ModalContext);

  const deleteMutation = useMutation({
    mutationFn: (orderId: number) => deleteSteelOrder(orderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['steelOrders'], refetchType: 'all' });
      setFeedback({ message: `Pedido ${order.id} excluído com sucesso!`, isOpen: true, statusOk: true });
      setShowDeleteModal(false);
    },
    onError: (error) => {
      console.error('Erro ao excluir pedido:', error);
      setFeedback({ message: 'Erro ao excluir pedido. Tente novamente.', isOpen: true, statusOk: false });
    },
  });

  const updateSaleStatusMutation = useMutation({
    mutationFn: ({ orderId, status }: { orderId: number; status: boolean | null }) => {
      console.log(`[FRONTEND] Solicitando atualização de status (fechar/cancelar) do pedido ${orderId}...`, status);
      return updateSteelOrder(orderId, { status });
    },
    onSuccess: (data) => {
      console.log(`[FRONTEND] Status atualizado com sucesso. Retorno da API:`, data);
      queryClient.invalidateQueries({ queryKey: ['steelOrders'], refetchType: 'all' });
      setShowCloseSaleModal(false);
    },
    onError: (error: any) => {
      console.error('Erro ao atualizar status da venda:', error);
      const rawMsg = error?.response?.data?.message;
      const backendMsg = Array.isArray(rawMsg) ? rawMsg.join(', ') : rawMsg;
      setFeedback({
        message: backendMsg || 'Erro ao atualizar o status da venda. Tente novamente.',
        isOpen: true,
        statusOk: false,
      });
    },
  });

  const closeSaleMutation = useMutation({
    mutationFn: (orderId: number) => {
      return closeSaleOrder(orderId);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['steelOrders'], refetchType: 'all' });
      setShowCloseSaleModal(false);
      setFeedback({
        message: 'Venda fechada com sucesso.',
        isOpen: true,
        statusOk: true,
      });
    },
    onError: (error: any) => {
      console.error('Erro ao fechar venda no front:', error);
      const rawMsg = error?.response?.data?.message;
      const backendMsg = Array.isArray(rawMsg) ? rawMsg.join(', ') : rawMsg;
      setFeedback({
        message: backendMsg || 'Erro ao fechar a venda. Verifique os dados do pedido.',
        isOpen: true,
        statusOk: false,
      });
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: (orderId: number) => duplicateSteelOrder(orderId),
    onSuccess: (duplicatedOrder) => { 
      localStorage.removeItem('reorderedData');
      localStorage.setItem('lastDuplicatedFrom', order.id.toString());
      
      queryClient.invalidateQueries({ queryKey: ['steelOrders'], refetchType: 'all' });
    },
    onError: (error) => {
      console.error('Erro ao duplicar pedido:', error);
    },
  });

  const handleAction = (action: string) => {
    switch (action) {
      case 'view':
        navigate(`/panel/steel/${order.id}`);
        break;
      case 'edit':
        navigate(`/panel/steel/edit/${order.id}`);
        break;
      case 'close-sale':
        setShowCloseSaleModal(true);
        break;
      case 'generate-pdf': {
        const generatePdfProgrammatically = async () => {
          try {
            setFeedback({ message: 'Gerando PDF, aguarde...', isOpen: true, statusOk: true });
            const { data: fullOrder } = await ApiService.steelOrders.getOrder(order.id);
            const { pdf } = await import('@react-pdf/renderer');
            const { default: OrderPDFDocument } = await import('../../components/OrderPDFDocument');
            const blob = await pdf(<OrderPDFDocument orderData={fullOrder} />).toBlob();
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
          } catch (error) {
            console.error('Erro ao gerar PDF:', error);
            setFeedback({ message: 'Erro ao gerar o PDF. Tente novamente.', isOpen: true, statusOk: false });
          }
        };
        generatePdfProgrammatically();
        break;
      }
      case 'generate-nf':
        setSelectedSteelOrder(order);
        setModalSection('generateOrderInvoice');
        setModalActive(true);
        break;
      case 'invoice':
        break;
      case 'commission':
        break;
      case 'send-email':
        break;
      case 'send-whatsapp':
        break;
      case 'duplicate':
        duplicateMutation.mutate(order.id);
        break;
      case 'delete':
        setShowDeleteModal(true);
        break;
      default:
        break;
    }
  };

  const handleConfirmDelete = () => {
    deleteMutation.mutate(order.id);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  const handleConfirmCloseSale = () => {
    updateSaleStatusMutation.mutate({ orderId: order.id, status: true });
  };

  const handleCancelSale = () => {
    updateSaleStatusMutation.mutate({ orderId: order.id, status: false });
  };

  const handleCancelCloseSale = () => {
    setShowCloseSaleModal(false);
  };

  const actions: RowAction[] = [
    ...(order.status === null || order.status === undefined
      ? [{ key: 'close-sale', label: 'Fechar venda', icon: <ShoppingCart size={16} />, onClick: () => handleAction('close-sale') }]
      : []),
    ...(order.status === true || order.status === false
      ? [{ key: 'revert-sale', label: 'Reverter venda', icon: <ShoppingCart size={16} />, onClick: () => handleAction('revert-sale') }]
      : []),
    { key: 'generate-pdf', label: 'Gerar PDF', icon: <FileText size={16} />, onClick: () => handleAction('generate-pdf') },
    ...(order.status === true
      ? [{ key: 'generate-nf', label: 'Gerar NF', icon: <Receipt size={16} />, onClick: () => handleAction('generate-nf') }]
      : []),
    { key: 'invoice', label: 'Faturar', icon: <DollarSign size={16} />, onClick: () => handleAction('invoice') },
    { key: 'commission', label: 'Comissão', icon: <Percent size={16} />, onClick: () => handleAction('commission') },
    { key: 'send-email', label: 'Enviar por e-mail', icon: <Mail size={16} />, onClick: () => handleAction('send-email') },
    { key: 'send-whatsapp', label: 'Enviar por Whatsapp', icon: <MessageSquare size={16} />, onClick: () => handleAction('send-whatsapp') },
    { key: 'view', label: 'Visualizar', icon: <Eye size={16} />, onClick: () => handleAction('view') },
    { key: 'duplicate', label: 'Duplicar', icon: <Copy size={16} />, onClick: () => handleAction('duplicate') },
    { key: 'edit', label: 'Editar', icon: <Edit size={16} />, onClick: () => handleAction('edit') },
    { key: 'delete', label: 'Excluir', icon: <Trash2 size={16} />, onClick: () => handleAction('delete') },
  ];

  return (
    <>
      <RowActionsMenu actions={actions} />
      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Excluir Pedido"
        message={`Tem certeza que deseja excluir o pedido ${order.id}? Esta ação não pode ser desfeita.`}
        actions={[
          {
            label: 'Cancelar',
            onClick: handleCancelDelete,
            variant: 'outline'
          },
          {
            label: deleteMutation.isPending ? 'Excluindo...' : 'Excluir',
            onClick: handleConfirmDelete,
            variant: 'danger'
          }
        ]}
      />
      <CloseSaleModal
        isOpen={showCloseSaleModal}
        onClose={handleCancelCloseSale}
        onConfirm={handleConfirmCloseSale}
        onCancelSale={handleCancelSale}
        orderId={order.id}
        isLoading={updateSaleStatusMutation.isPending}
      />
    </>
  );
};

export default ActionsDropdown;
